/**
 * Matomo event tracking.
 *
 * Implements all events from Events_Tracking.csv on top of the Matomo
 * snippet already present in header.php. Reads dimension IDs and page
 * groups from window.lmTracking (printed by inc/matomo-tracking.php).
 *
 * Event taxonomy / parameter handling is documented in docs/matomo-tracking.md.
 */

(function ($) {
	'use strict';

	const cfg = (window.lmTracking = window.lmTracking || {});
	const DIMS = cfg.dimensions || {};
	const PAGE_GROUPS = cfg.pageGroups || {};

	/* --------------------------------------------------------------------- *
	 * Low-level helper
	 * --------------------------------------------------------------------- */

	/**
	 * Push to Matomo's queue.
	 *
	 * IMPORTANT: never cache window._paq. Once matomo.js initialises it
	 * replaces window._paq with its own tracker proxy object; a reference
	 * captured earlier would point at the original (now abandoned) array
	 * and every push would be silently dropped. Always resolve window._paq
	 * at call time.
	 */
	function paqPush(args) {
		(window._paq = window._paq || []).push(args);
	}

	/**
	 * Push a Matomo trackEvent with optional Custom Dimensions.
	 * Dimensions are set immediately before the event and cleared
	 * immediately after so they don't leak into later events.
	 */
	function track(category, action, name, value, dims) {
		const dimKeys = dims ? Object.keys(dims) : [];

		dimKeys.forEach(function (key) {
			const id = DIMS[key];
			const val = dims[key];
			if (id && val !== undefined && val !== null && val !== '') {
				paqPush(['setCustomDimension', id, String(val)]);
			}
		});

		const args = ['trackEvent', category, action];
		if (name !== undefined && name !== null) args.push(String(name));
		if (value !== undefined && value !== null) args.push(value);
		paqPush(args);

		dimKeys.forEach(function (key) {
			const id = DIMS[key];
			if (id) paqPush(['deleteCustomDimension', id]);
		});
	}

	function currentHref() {
		try {
			return window.location.href;
		} catch (e) {
			return '';
		}
	}

	function urlMatchesGroup(groupKey) {
		const list = PAGE_GROUPS[groupKey];
		if (!Array.isArray(list) || !list.length) return false;
		const href = currentHref().toLowerCase();
		for (let i = 0; i < list.length; i++) {
			if (href.indexOf(String(list[i]).toLowerCase()) !== -1) return true;
		}
		return false;
	}

	/* --------------------------------------------------------------------- *
	 * scroll: 25 / 50 / 75 / 100
	 * --------------------------------------------------------------------- */

	(function initScrollDepth() {
		const thresholds = [25, 50, 75, 100];
		let fired = {};

		function reset() { fired = {}; }

		function onScroll() {
			const doc = document.documentElement;
			const body = document.body;
			const scrollTop = window.pageYOffset || doc.scrollTop || body.scrollTop || 0;
			const viewport = window.innerHeight || doc.clientHeight;
			const fullHeight = Math.max(
				doc.scrollHeight, body ? body.scrollHeight : 0,
				doc.offsetHeight, body ? body.offsetHeight : 0
			);
			const scrollable = Math.max(fullHeight - viewport, 1);
			const pct = Math.min(100, Math.round((scrollTop / scrollable) * 100));

			for (let i = 0; i < thresholds.length; i++) {
				const t = thresholds[i];
				if (!fired[t] && pct >= t) {
					fired[t] = true;
					track('Engagement', 'scroll', String(t), t);
				}
			}
		}

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });
		// Some single-page nav (WPML lang switch) keeps the listener; reset on visibilitychange to a fresh tab.
		window.addEventListener('pageshow', reset);
	})();

	/* --------------------------------------------------------------------- *
	 * Click tracking: button / header_button / cta / link / mail / file_download
	 * --------------------------------------------------------------------- */

	const DOWNLOAD_EXTENSIONS = [
		'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
		'zip', 'rar', 'csv', 'txt', 'mp3', 'mp4', 'mov', 'wav'
	];

	function getLinkExtension(url) {
		if (!url) return '';
		const clean = url.split('?')[0].split('#')[0];
		const match = clean.match(/\.([a-z0-9]+)$/i);
		if (!match) return '';
		const ext = match[1].toLowerCase();
		return DOWNLOAD_EXTENSIONS.indexOf(ext) !== -1 ? ext : '';
	}

	function getFileName(url) {
		if (!url) return '';
		const clean = url.split('?')[0].split('#')[0];
		const parts = clean.split('/');
		return parts[parts.length - 1] || clean;
	}

	function getDomain(url) {
		try {
			return new URL(url, window.location.origin).hostname;
		} catch (e) {
			return '';
		}
	}

	function isPrimaryCta(el) {
		if (!el || !el.classList) return false;
		// Primary CTAs: orange primary buttons, CTA-block buttons, primary buttons inside covers.
		if (el.classList.contains('button--primary')) return true;
		if (el.closest('.cta--wrap') && (el.classList.contains('button--white') || el.classList.contains('button--primary'))) return true;
		return false;
	}

	function isButton(el) {
		if (!el || !el.classList) return false;
		const cl = el.classList;
		return (
			el.tagName === 'BUTTON' ||
			cl.contains('button--primary') ||
			cl.contains('button--outline') ||
			cl.contains('button--outline--primary') ||
			cl.contains('button--white') ||
			cl.contains('button--dark') ||
			cl.contains('button--ghost') ||
			cl.contains('button--arrow')
		);
	}

	document.addEventListener('click', function (event) {
		// Find the closest interactive element.
		const target = event.target;
		if (!target || !target.closest) return;

		const el = target.closest('a, button');
		if (!el) return;

		const text = (el.textContent || el.getAttribute('aria-label') || '').trim().replace(/\s+/g, ' ').slice(0, 200);
		const href = el.tagName === 'A' ? (el.getAttribute('href') || '') : '';
		const inHeader = !!el.closest('header.site-header');

		// mail_click — highest specificity for mailto links.
		if (href && href.toLowerCase().indexOf('mailto:') === 0) {
			track('Interaction', 'mail_click', href, null, {
				link_url: href
			});
			return;
		}

		// file_download — specific extension list.
		if (href) {
			const ext = getLinkExtension(href);
			if (ext) {
				track('Download', 'file_download', getFileName(href), null, {
					link_url: href,
					file_extension: ext
				});
				return;
			}
		}

		// Regular links AND/OR buttons.
		const treatedAsButton = isButton(el);

		if (treatedAsButton) {
			track('Interaction', 'button_click', text, null, {
				link_url: href,
				link_text: text
			});

			if (inHeader) {
				track('Interaction', 'header_button_click', text, null, {
					link_url: href,
					link_text: text
				});
			}

			if (isPrimaryCta(el)) {
				track('Conversion', 'cta_click', text, null, {
					link_url: href,
					link_text: text
				});
			}
		}

		// link_click fires for every <a> (including buttons styled as links) — global click event per CSV.
		if (el.tagName === 'A' && href) {
			track('Interaction', 'link_click', text, null, {
				link_url: href,
				link_text: text,
				link_domain: getDomain(href)
			});
		}
	}, true);

	/* --------------------------------------------------------------------- *
	 * Gravity Forms — successful submit
	 * --------------------------------------------------------------------- */

	$(document).on('gform_confirmation_loaded', function (event, formId) {
		const formName = 'gform_' + formId;

		track('Form', 'form_submit', formName, null, {
			form_name: formName,
			form_type: 'gravity'
		});

		// Newsletter form lives on /newsletter/ pages (and form IDs 2/3/4 are the WPML newsletter variants).
		const isNewsletterById = (formId === 2 || formId === 3 || formId === 4 || formId === '2' || formId === '3' || formId === '4');
		if (urlMatchesGroup('newsletter') || isNewsletterById) {
			track('Conversion', 'newsletter_sign_up', 'newsletter_signup', null, {
				form_name: 'newsletter_signup',
				method: 'newsletter'
			});

			track('Conversion', 'form_submit_newsletter', 'newsletter_signup', null, {
				form_name: 'newsletter_signup',
				method: 'newsletter'
			});
		}
	});

	/* --------------------------------------------------------------------- *
	 * Typeform — successful submit (postMessage)
	 * --------------------------------------------------------------------- */

	window.addEventListener('message', function (event) {
		if (!event || !event.data) return;
		const data = event.data;
		const type = (typeof data === 'string') ? data : (data.type || data.event_type || '');
		if (!type) return;

		// Typeform emits 'form-submit' / 'tf-form-submit' / 'submit' depending on embed version.
		if (String(type).indexOf('form-submit') === -1 && String(type).indexOf('submit') === -1) return;

		// Baseline event.
		track('Form', 'form_submit', 'typeform', null, {
			form_name: 'typeform',
			form_type: 'typeform'
		});

		if (urlMatchesGroup('mentor')) {
			track('Conversion', 'form_submit_mentor', 'mentor_application', null, {
				form_name: 'mentor_application',
				form_type: 'typeform'
			});
		}

		if (urlMatchesGroup('teilnehmer')) {
			track('Conversion', 'form_submit_teilnehmer', 'participant_application', null, {
				form_name: 'participant_application',
				form_type: 'typeform'
			});
		}
	});

	/* --------------------------------------------------------------------- *
	 * Video — YouTube / Vimeo / local video.
	 *
	 * All theme videos open in GLightbox (template-parts/blocks/block-video.php
	 * uses <a class="glightbox">). GLightbox 3.x plays every provider through
	 * Plyr, which normalises events (playing / timeupdate / ended) and time
	 * (currentTime / duration) across YouTube, Vimeo and local <video>. We
	 * therefore hook Plyr via the GLightbox instance rather than wiring the
	 * YouTube IFrame API and Vimeo Player API separately.
	 *
	 * Consent: GLightbox configures Plyr with youtube.noCookie = true
	 * (youtube-nocookie.com), so no YouTube cookies are set. Events are still
	 * sent through _paq like every other Matomo event.
	 *
	 * Inline (non-lightbox) HTML5 <video> elements are tracked directly; any
	 * <video> managed by Plyr/GLightbox is skipped to avoid double counting.
	 * --------------------------------------------------------------------- */

	const VIDEO_HREF_RE = /youtube\.com|youtu\.be|youtube-nocookie\.com|vimeo\.com|\.(mp4|webm|ogv|mov)(\?|#|$)/i;

	function findVideoTitle(el) {
		if (!el) return '';
		const direct = el.getAttribute('data-title') || el.getAttribute('aria-label') || el.getAttribute('title');
		if (direct) return direct.trim();
		const labelled = el.closest && el.closest('[data-video-title]');
		if (labelled) return (labelled.getAttribute('data-video-title') || '').trim();
		// Fall back to nearby block headline (e.g. block--video sits in a content section with a preceding h2).
		const section = el.closest && el.closest('section, .block__container, .content__section');
		if (section) {
			const h = section.querySelector('h1, h2, h3');
			if (h) return (h.textContent || '').trim().slice(0, 200);
		}
		return '';
	}

	/**
	 * Attach Matomo tracking to a Plyr instance (YouTube / Vimeo / local).
	 * Emits video_start (first playback), video_progress (25/50/75) and
	 * video_complete. Bound once per player instance.
	 */
	function attachPlyrTracking(player, meta) {
		if (!player || player.__lmMatomoBound) return;
		if (typeof player.on !== 'function') return;
		player.__lmMatomoBound = true;

		const title = (meta && meta.title) || 'video';
		const url = (meta && meta.url) || '';
		const fired = { start: false, 25: false, 50: false, 75: false, complete: false };

		function percent() {
			const d = player.duration;
			const c = player.currentTime;
			if (!d || !isFinite(d) || d <= 0) return -1;
			return Math.floor((c / d) * 100);
		}

		player.on('playing', function () {
			if (fired.start) return;
			fired.start = true;
			track('Video', 'video_start', title, null, {
				video_title: title,
				link_url: url
			});
		});

		player.on('timeupdate', function () {
			const p = percent();
			if (p < 0) return;
			[25, 50, 75].forEach(function (t) {
				if (!fired[t] && p >= t) {
					fired[t] = true;
					track('Video', 'video_progress', title, t, {
						video_title: title,
						video_progress: t
					});
				}
			});
		});

		player.on('ended', function () {
			if (fired.complete) return;
			fired.complete = true;
			track('Video', 'video_complete', title, 100, {
				video_title: title,
				link_url: url
			});
		});
	}

	/**
	 * Hook a GLightbox instance: when a video slide opens, resolve its Plyr
	 * instance (loaded asynchronously) and attach tracking.
	 */
	function bindLightboxVideoTracking(lightbox) {
		if (!lightbox || typeof lightbox.on !== 'function' || lightbox.__lmMatomoBound) return;
		lightbox.__lmMatomoBound = true;

		lightbox.on('slide_changed', function (payload) {
			const current = payload && payload.current;
			if (!current) return;

			const trigger = current.trigger || null;
			const href = trigger ? (trigger.getAttribute('href') || '') : '';

			// Only act on video slides.
			if (href && !VIDEO_HREF_RE.test(href)) return;

			const meta = {
				title: findVideoTitle(trigger) || 'video',
				url: href
			};

			// Plyr loads from CDN asynchronously; poll for the instance.
			let tries = 0;
			(function waitForPlayer() {
				let player = current.player;
				if (!player && typeof lightbox.getSlidePlayerInstance === 'function') {
					player = lightbox.getSlidePlayerInstance(current.index);
				}
				if (player) {
					attachPlyrTracking(player, meta);
					return;
				}
				if (tries++ < 80) setTimeout(waitForPlayer, 150);
			})();
		});
	}

	function isPlyrManaged(video) {
		return !!(video.closest && video.closest('.gvideo-wrapper, .plyr, .gslide-media'));
	}

	function attachHtml5Tracking(video) {
		if (!video || video.__lmMatomoBound) return;
		// Videos played through Plyr/GLightbox are tracked via attachPlyrTracking.
		if (isPlyrManaged(video)) return;
		video.__lmMatomoBound = true;

		const title = findVideoTitle(video) || (video.currentSrc || '').split('/').pop() || 'video';
		const url = video.currentSrc || video.src || '';
		const fired = { start: false, 25: false, 50: false, 75: false, complete: false };

		video.addEventListener('play', function () {
			if (fired.start) return;
			fired.start = true;
			track('Video', 'video_start', title, null, {
				video_title: title,
				link_url: url
			});
		});

		video.addEventListener('timeupdate', function () {
			if (!video.duration || !isFinite(video.duration)) return;
			const pct = Math.floor((video.currentTime / video.duration) * 100);
			[25, 50, 75].forEach(function (t) {
				if (!fired[t] && pct >= t) {
					fired[t] = true;
					track('Video', 'video_progress', title, t, {
						video_title: title,
						video_progress: t
					});
				}
			});
		});

		video.addEventListener('ended', function () {
			if (fired.complete) return;
			fired.complete = true;
			track('Video', 'video_complete', title, 100, {
				video_title: title,
				link_url: url
			});
		});
	}

	function scanForVideos(root) {
		(root || document).querySelectorAll('video').forEach(attachHtml5Tracking);
	}

	/* --------------------------------------------------------------------- *
	 * Embedded <iframe> videos (YouTube / Vimeo) — e.g. oEmbed / WYSIWYG
	 * embeds that do NOT go through GLightbox/Plyr.
	 *
	 * YouTube: needs the IFrame Player API and enablejsapi=1 on the embed
	 * URL. We add the param if missing (reloads the iframe once), then attach
	 * a YT.Player and poll currentTime/duration for progress.
	 *
	 * Vimeo: uses the Vimeo Player API, which exposes play / timeupdate
	 * (with .percent) / ended directly.
	 *
	 * Iframes already managed by Plyr/GLightbox are skipped (handled above).
	 * --------------------------------------------------------------------- */

	const YT_IFRAME_RE = /youtube\.com|youtu\.be|youtube-nocookie\.com/i;
	const VIMEO_IFRAME_RE = /player\.vimeo\.com|vimeo\.com/i;
	let lmIframeSeq = 0;

	function iframeIsPlyrManaged(iframe) {
		return !!(iframe.closest && iframe.closest('.gvideo-wrapper, .plyr, .gslide-media'));
	}

	function iframeTitle(iframe, fallback) {
		return iframe.getAttribute('title') || findVideoTitle(iframe) || fallback;
	}

	// ---- YouTube IFrame API ----
	let ytApiState = 'idle'; // idle | loading | ready
	const ytReadyCbs = [];

	/**
	 * Run cb once the YouTube IFrame API is ready. Robust against the API
	 * already being present (in which case onYouTubeIframeAPIReady would
	 * never fire again).
	 */
	function whenYtApiReady(cb) {
		if (window.YT && window.YT.Player) {
			ytApiState = 'ready';
			cb();
			return;
		}
		ytReadyCbs.push(cb);
		loadYouTubeApi();
	}

	function loadYouTubeApi() {
		if (ytApiState === 'ready' || ytApiState === 'loading') return;
		ytApiState = 'loading';
		const prev = window.onYouTubeIframeAPIReady;
		window.onYouTubeIframeAPIReady = function () {
			if (typeof prev === 'function') { try { prev(); } catch (e) {} }
			ytApiState = 'ready';
			ytReadyCbs.splice(0).forEach(function (cb) { try { cb(); } catch (e) {} });
		};
		const tag = document.createElement('script');
		tag.src = 'https://www.youtube.com/iframe_api';
		(document.head || document.body).appendChild(tag);
	}

	function trackYtIframe(iframe) {
		if (!iframe || iframe.__lmMatomoBound || iframeIsPlyrManaged(iframe)) return;
		const src = iframe.getAttribute('src') || '';
		if (!src || !YT_IFRAME_RE.test(src)) return;
		iframe.__lmMatomoBound = true;

		if (!iframe.id) iframe.id = 'lm-yt-' + (++lmIframeSeq);

		// The IFrame API only emits events when enablejsapi=1 is present.
		// Adding it reloads the iframe, so the YT.Player MUST be created only
		// after that reload completes (load event) — otherwise the player's
		// event callbacks bind to the wrong/transitional content window and
		// never fire (the handshake still happens, but our handlers are lost).
		if (!/[?&]enablejsapi=1/.test(src)) {
			const sep = src.indexOf('?') === -1 ? '?' : '&';
			let newSrc = src + sep + 'enablejsapi=1';
			try { newSrc += '&origin=' + encodeURIComponent(window.location.origin); } catch (e) {}

			const onLoad = function () {
				iframe.removeEventListener('load', onLoad);
				whenYtApiReady(function () { createYtPlayer(iframe); });
			};
			iframe.addEventListener('load', onLoad);
			iframe.setAttribute('src', newSrc);
		} else {
			// Already API-enabled and loaded with the page.
			whenYtApiReady(function () { createYtPlayer(iframe); });
		}
	}

	/* YouTube event tracking is driven by the IFrame API's postMessage stream
	 * rather than YT.Player JS callbacks: on reloaded/oEmbed iframes the
	 * constructor's onStateChange callback proved unreliable (the handshake
	 * completes and messages flow, but the bound callback never fires). We
	 * therefore parse the raw "infoDelivery"/"onStateChange" messages — which
	 * we verified do stream — and still create a YT.Player purely to trigger
	 * the "listening" handshake that starts that stream. Both paths share the
	 * same per-iframe `fired` flags so events are never counted twice. */

	const ytEntries = []; // { win, iframe, title, url, fired }
	let ytMsgBound = false;

	function fireYtEvent(entry, state, cur, dur) {
		const name = entry.title || 'youtube';
		if (state === 1 && !entry.fired.start) {
			entry.fired.start = true;
			track('Video', 'video_start', name, null, { video_title: name, link_url: entry.url });
		}
		if (cur != null && dur && dur > 0) {
			const pct = Math.floor((cur / dur) * 100);
			[25, 50, 75].forEach(function (t) {
				if (!entry.fired[t] && pct >= t) {
					entry.fired[t] = true;
					track('Video', 'video_progress', name, t, { video_title: name, video_progress: t });
				}
			});
		}
		if (state === 0 && !entry.fired.complete) {
			entry.fired.complete = true;
			track('Video', 'video_complete', name, 100, { video_title: name, link_url: entry.url });
		}
	}

	function bindYtMessageListener() {
		if (ytMsgBound) return;
		ytMsgBound = true;
		window.addEventListener('message', function (e) {
			if (typeof e.data !== 'string' || e.data.indexOf('"event"') === -1) return;
			let data;
			try { data = JSON.parse(e.data); } catch (err) { return; }
			if (!data || (data.event !== 'infoDelivery' && data.event !== 'onStateChange')) return;

			let entry = null;
			for (let i = 0; i < ytEntries.length; i++) {
				if (ytEntries[i].win && ytEntries[i].win === e.source) { entry = ytEntries[i]; break; }
			}
			if (!entry) return;

			let state, cur, dur;
			const info = (data.info && typeof data.info === 'object') ? data.info : null;
			if (data.event === 'onStateChange' && typeof data.info === 'number') state = data.info;
			if (info) {
				if (typeof info.playerState !== 'undefined') state = info.playerState;
				if (typeof info.currentTime !== 'undefined') cur = info.currentTime;
				if (typeof info.duration !== 'undefined') dur = info.duration;
				if (info.videoData && info.videoData.title) entry.title = info.videoData.title;
			}
			fireYtEvent(entry, state, cur, dur);
		}, false);
	}

	function createYtPlayer(iframe) {
		if (!window.YT || !window.YT.Player) return;
		if (iframe.__lmYtPlayer) return;

		const url = iframe.getAttribute('src') || '';
		const entry = {
			win: iframe.contentWindow,
			iframe: iframe,
			title: iframeTitle(iframe, ''),
			url: url,
			fired: { start: false, 25: false, 50: false, 75: false, complete: false }
		};
		ytEntries.push(entry);
		bindYtMessageListener();

		try {
			const player = new window.YT.Player(iframe, {
				events: {
					onReady: function () {
						entry.win = iframe.contentWindow;
						try {
							const vd = player.getVideoData();
							if (vd && vd.title) entry.title = vd.title;
						} catch (e) {}
					},
					// Backup path; shares entry.fired so it can't double-count.
					onStateChange: function (e) {
						let cur, dur;
						try { dur = player.getDuration(); cur = player.getCurrentTime(); } catch (err) {}
						if (!entry.title) {
							try { const vd = player.getVideoData(); if (vd && vd.title) entry.title = vd.title; } catch (err2) {}
						}
						fireYtEvent(entry, e.data, cur, dur);
					}
				}
			});
			iframe.__lmYtPlayer = player;
		} catch (e) {}
	}

	// ---- Vimeo Player API ----
	let vimeoApiState = 'idle';
	const vimeoQueue = [];

	function loadVimeoApi() {
		if (window.Vimeo && window.Vimeo.Player) {
			vimeoApiState = 'ready';
			vimeoQueue.splice(0).forEach(createVimeoPlayer);
			return;
		}
		if (vimeoApiState === 'loading') return;
		vimeoApiState = 'loading';
		const tag = document.createElement('script');
		tag.src = 'https://player.vimeo.com/api/player.js';
		tag.onload = function () {
			vimeoApiState = 'ready';
			vimeoQueue.splice(0).forEach(createVimeoPlayer);
		};
		(document.head || document.body).appendChild(tag);
	}

	function trackVimeoIframe(iframe) {
		if (!iframe || iframe.__lmMatomoBound || iframeIsPlyrManaged(iframe)) return;
		const src = iframe.getAttribute('src') || '';
		if (!src || !VIMEO_IFRAME_RE.test(src)) return;
		iframe.__lmMatomoBound = true;

		if (window.Vimeo && window.Vimeo.Player) {
			createVimeoPlayer(iframe);
		} else {
			vimeoQueue.push(iframe);
			loadVimeoApi();
		}
	}

	function createVimeoPlayer(iframe) {
		if (!window.Vimeo || !window.Vimeo.Player) return;
		const url = iframe.getAttribute('src') || '';
		const fired = { start: false, 25: false, 50: false, 75: false, complete: false };
		let title = iframeTitle(iframe, 'vimeo');

		try {
			const player = new window.Vimeo.Player(iframe);
			player.getVideoTitle().then(function (t) { if (t) title = t; }).catch(function () {});

			player.on('play', function () {
				if (fired.start) return;
				fired.start = true;
				track('Video', 'video_start', title, null, { video_title: title, link_url: url });
			});

			player.on('timeupdate', function (data) {
				const pct = Math.floor((data && data.percent ? data.percent : 0) * 100);
				[25, 50, 75].forEach(function (t) {
					if (!fired[t] && pct >= t) {
						fired[t] = true;
						track('Video', 'video_progress', title, t, { video_title: title, video_progress: t });
					}
				});
			});

			player.on('ended', function () {
				if (fired.complete) return;
				fired.complete = true;
				track('Video', 'video_complete', title, 100, { video_title: title, link_url: url });
			});
		} catch (e) {}
	}

	function processIframe(iframe) {
		const src = iframe.getAttribute('src') || '';
		if (!src) return;
		if (YT_IFRAME_RE.test(src)) trackYtIframe(iframe);
		else if (VIMEO_IFRAME_RE.test(src)) trackVimeoIframe(iframe);
	}

	function scanForIframeVideos(root) {
		if (!root) root = document;
		if (root.tagName === 'IFRAME') { processIframe(root); return; }
		if (root.querySelectorAll) root.querySelectorAll('iframe').forEach(processIframe);
	}

	$(function () {
		scanForVideos(document);
		scanForIframeVideos(document);

		// GLightbox main instance is exposed by _glightbox.js (window.lmLightbox).
		if (window.lmLightbox) {
			bindLightboxVideoTracking(window.lmLightbox);
		} else {
			let waited = 0;
			const iv = setInterval(function () {
				if (window.lmLightbox) {
					bindLightboxVideoTracking(window.lmLightbox);
					clearInterval(iv);
				} else if (waited++ > 50) {
					clearInterval(iv);
				}
			}, 100);
		}

		// Re-scan after DOM mutations for inline HTML5 videos and embedded
		// iframes (e.g. lazy-loaded embeds).
		if (typeof MutationObserver !== 'undefined') {
			const mo = new MutationObserver(function (mutations) {
				for (let i = 0; i < mutations.length; i++) {
					const m = mutations[i];
					for (let j = 0; j < m.addedNodes.length; j++) {
						const node = m.addedNodes[j];
						if (node && node.nodeType === 1) {
							if (node.tagName === 'VIDEO') attachHtml5Tracking(node);
							else if (node.tagName === 'IFRAME') scanForIframeVideos(node);
							else if (node.querySelectorAll) {
								scanForVideos(node);
								scanForIframeVideos(node);
							}
						}
					}
				}
			});
			mo.observe(document.body, { childList: true, subtree: true });
		}
	});

})(window.jQuery);
